import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { SpiderwebLink } from "@/components/interlinking/SpiderwebLink";
import { RelatedInterlinks } from "@/components/interlinking/RelatedInterlinks";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { defaultSchema } from 'hast-util-sanitize';
import { Clock, Calendar, ArrowLeft, MessageSquare, ThumbsUp, ThumbsDown, Star, MapPin, Play, Home } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const defaultListingImg = "/default-listing-fallback.png";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import SEO from "@/components/SEO";

import { toast } from "sonner";
import { generateArticleSchema, generateBreadcrumbSchema, generateFAQSchema } from "@/utils/seo";
import { SITE_URL } from "@/constants/seo";
import { generateArticleMetaDescription } from "@/utils/metaDescriptions";
import { generateArticleTitle } from "@/utils/seoTitles";
import Footer from "@/components/Footer";
import { ProgramCTA } from "@/components/content/ProgramCTA";
import { EmbeddedListings } from "@/components/articles/EmbeddedListings";
import React from "react";

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'u', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'tfoot', 'caption'],
  attributes: {
    ...(defaultSchema.attributes || {}),
    td: ['colspan', 'rowspan', 'align'],
    th: ['colspan', 'rowspan', 'align', 'scope'],
    table: ['border', 'cellpadding', 'cellspacing'],
  },
};

/** Max in-text links for the entire article body */
const ARTICLE_MAX_INLINE_LINKS = 4;

/**
 * Renders article markdown with a global cap of ARTICLE_MAX_INLINE_LINKS interlinks.
 * Each render creates a fresh counter so SpiderwebLink calls share a single budget.
 */
const ArticleMarkdownBody = ({ markdown }: { markdown: string }) => {
  const linksRef = useRef(0);
  // Reset counter every render (article change)
  linksRef.current = 0;

  const processChildren = useCallback((children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, (child) => {
      if (typeof child === "string" && child.trim().length > 0 && linksRef.current < ARTICLE_MAX_INLINE_LINKS) {
        const remaining = ARTICLE_MAX_INLINE_LINKS - linksRef.current;
        linksRef.current += remaining; // reserve slots so next elements get 0
        return <SpiderwebLink text={child} maxLinks={remaining} />;
      }
      return child;
    });
  }, []);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
      components={{
        p: ({node, children, ...props}) => <p {...props}>{processChildren(children)}</p>,
        li: ({node, children, ...props}) => <li {...props}>{processChildren(children)}</li>,
        table: ({node, ...props}) => <table className="w-full border-collapse border border-border my-6" {...props} />,
        thead: ({node, ...props}) => <thead className="bg-muted" {...props} />,
        th: ({node, ...props}) => <th className="border border-border px-4 py-2 text-left font-semibold" {...props} />,
        td: ({node, ...props}) => <td className="border border-border px-4 py-2" {...props} />,
        u: ({node, ...props}) => <u {...props} />,
      }}
    >
      {markdown}
    </ReactMarkdown>
  );
};

const Article = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState<any>(null);
  const [category, setCategory] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [relatedArticles, setRelatedArticles] = useState<any[]>([]);
  const [relatedListings, setRelatedListings] = useState<any[]>([]);
  const [relatedVideos, setRelatedVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentName, setCommentName] = useState("");
  const [commentEmail, setCommentEmail] = useState("");
  const [commentText, setCommentText] = useState("");
  const [userVote, setUserVote] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      const { data: articleData, error } = await supabase
        .from('articles')
        .select('*, category:categories(*)')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();

      if (error) throw error;

      if (!articleData) {
        setArticle(null);
        setLoading(false);
        return;
      }

      setArticle(articleData);
      setCategory(articleData.category);

      // Load comments
      const { data: commentsData } = await supabase
        .from('article_comments_public')
        .select('*')
        .eq('article_id', articleData.id)
        .order('created_at', { ascending: false });

      setComments(commentsData || []);

      // Load related articles (same category, exclude current)
      const { data: relatedArticlesData } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .eq('category_id', articleData.category_id)
        .neq('id', articleData.id)
        .limit(3);

      setRelatedArticles(relatedArticlesData || []);

      // Load related videos - prioritize by category match
      const { data: videosData } = await supabase
        .from('videos')
        .select('*')
        .eq('activo', true)
        .limit(50);

      // Sort by relevance: prioritize videos with matching category
      const sortedVideos = (videosData || [])
        .sort((a, b) => {
          const categoryMatch = (v: any) => {
            const articleCat = articleData.category?.name?.toLowerCase() || '';
            const videoCat = v.categoria?.toLowerCase() || '';
            return videoCat.includes(articleCat) || articleCat.includes(videoCat) ? 1 : 0;
          };
          return categoryMatch(b) - categoryMatch(a);
        })
        .slice(0, 3);
      
      setRelatedVideos(sortedVideos);

      // Load related listings - prioritize by city, then by category
      const { data: listingsData } = await supabase
        .from('listings')
        .select('*')
        .eq('status', 'active')
        .limit(50);

      // Sort by relevance: city first, then category
      const sortedListings = (listingsData || [])
        .sort((a, b) => {
          // City match is most important
          const cityMatchA = a.city?.toLowerCase() === article?.city?.toLowerCase() ? 2 : 0;
          const cityMatchB = b.city?.toLowerCase() === article?.city?.toLowerCase() ? 2 : 0;
          
          // Category match is secondary
          const categoryMatchA = a.type?.toLowerCase().includes(articleData.category?.name?.toLowerCase() || '') ? 1 : 0;
          const categoryMatchB = b.type?.toLowerCase().includes(articleData.category?.name?.toLowerCase() || '') ? 1 : 0;
          
          return (cityMatchB + categoryMatchB) - (cityMatchA + categoryMatchA);
        })
        .slice(0, 3);
      
      setRelatedListings(sortedListings);
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (voteType: 'up' | 'down') => {
    if (userVote === voteType) {
      // Remove vote
      const updateField = voteType === 'up' ? 'votos_positivos' : 'votos_negativos';
      await supabase
        .from('articles')
        .update({ [updateField]: article[updateField] - 1 })
        .eq('id', article.id);
      
      setUserVote(null);
      setArticle({ ...article, [updateField]: article[updateField] - 1 });
    } else {
      // Add or change vote
      const previousVote = userVote;
      const newUpVotes = voteType === 'up' 
        ? article.votos_positivos + 1 
        : previousVote === 'up' 
          ? article.votos_positivos - 1 
          : article.votos_positivos;
      const newDownVotes = voteType === 'down' 
        ? article.votos_negativos + 1 
        : previousVote === 'down' 
          ? article.votos_negativos - 1 
          : article.votos_negativos;

      await supabase
        .from('articles')
        .update({ 
          votos_positivos: newUpVotes, 
          votos_negativos: newDownVotes 
        })
        .eq('id', article.id);

      setUserVote(voteType);
      setArticle({ ...article, votos_positivos: newUpVotes, votos_negativos: newDownVotes });
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!commentName || !commentText) {
      toast.error('Nombre y comentario son requeridos');
      return;
    }

    try {
      await supabase.from('article_comments').insert({
        article_id: article.id,
        user_name: commentName,
        user_email: commentEmail,
        comment: commentText
      });

      toast.success('Comentario enviado. Será visible una vez aprobado.');
      setCommentName("");
      setCommentEmail("");
      setCommentText("");
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error('Error al enviar comentario');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 pt-32 lg:pt-40 pb-12">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-muted rounded w-1/2 mb-8"></div>
            <div className="h-96 bg-muted rounded"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 pt-32 lg:pt-40 pb-12 text-center">
          <h1 className="text-4xl font-bold mb-4">Artículo no encontrado</h1>
          <Link to="/articles">
            <Button>Ver todos los artículos</Button>
          </Link>
        </main>
      </div>
    );
  }

  const articleUrl = `${SITE_URL}/articulo/${article.slug}`;
  
  // Ensure absolute URL and handle expired/invalid URLs
  const getValidImageUrl = (url: string | null | undefined): string => {
    if (!url) return `${SITE_URL}/og-image.png`;
    if (url.startsWith('http')) {
      // Check if it's an expired DALL-E URL (contains expiration parameters)
      if (url.includes('blob.core.windows.net') && url.includes('se=')) {
        return `${SITE_URL}/og-image.png`;
      }
      return url;
    }
    return `${SITE_URL}${url}`;
  };
  
  // Use nullish coalescing to properly handle null/undefined values
  const articleImageUrl = article.thumbnail_url ?? article.image_url ?? article.featured_image;
  const absoluteImageUrl = getValidImageUrl(articleImageUrl);

  const structuredData = [
    generateArticleSchema({
      title: article.title,
      description: article.meta_description || article.summary,
      image: absoluteImageUrl,
      datePublished: article.publish_date || article.created_at,
      dateModified: article.updated_at,
      url: articleUrl,
    }),
    generateBreadcrumbSchema([
      { name: "Inicio", url: SITE_URL },
      { name: "Artículos", url: `${SITE_URL}/articulos` },
      { name: category?.name || "General", url: `${SITE_URL}/articulos?category=${category?.slug || 'general'}` },
      { name: article.title, url: articleUrl },
    ]),
  ];

  // Generate unique meta description
  const metaDescription = generateArticleMetaDescription({
    title: article.title,
    meta_description: article.meta_description,
    summary: article.summary,
    tags: article.tags,
    reading_time: article.reading_time,
    category: category?.name,
    votos_positivos: article.votos_positivos,
    publish_date: article.publish_date,
    created_at: article.created_at
  });

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={generateArticleTitle({
          title: article.title,
          category: category?.name || 'Fitness',
          readingTime: article.reading_time
        })}
        description={metaDescription}
        keywords={[
          ...(article.tags || []),
          category?.name || 'fitness',
          'guía',
          'consejos',
        ]}
        ogType="article"
        ogImage={absoluteImageUrl}
        canonical={articleUrl}
        structuredData={structuredData}
      />
      
      <Navigation />

      <main className="container mx-auto px-4 pt-32 lg:pt-40 pb-12">
        {/* Breadcrumb Navigation */}
        <div className="max-w-7xl mx-auto mb-6">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">
                    <Home className="h-4 w-4" />
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/articles">Artículos</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              {category && (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={`/articles?category=${category.slug}`}>
                        {category.name}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                </>
              )}
              <BreadcrumbItem>
                <BreadcrumbPage className="max-w-[200px] truncate sm:max-w-none">
                  {article.title}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="max-w-7xl mx-auto">
          <Link to="/articles" className="inline-flex items-center text-primary hover:underline mb-6 mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a artículos
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {articleImageUrl && (
                <img
                  src={articleImageUrl}
                  alt={article.title}
                  className="w-full aspect-video object-cover rounded-lg mb-8"
                  width="1200"
                  height="675"
                  {...{ fetchpriority: 'high' } as any}
                  loading="eager"
                />
              )}

              <div className="flex flex-wrap gap-2 mb-4">
                {category && (
                  <Badge variant="secondary">
                    <span className="mr-1">{category.icon}</span>
                    {category.name}
                  </Badge>
                )}
                <Badge variant="outline" className="gap-1">
                  <Clock className="w-3 h-3" />
                  {article.reading_time} min lectura
                </Badge>
                <Badge variant="outline" className="gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(article.publish_date || article.created_at).toLocaleDateString('es-ES')}
                </Badge>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-6">{article.title}</h1>

              {/* Rating Display */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground">5.0 Valoración</span>
              </div>

              {article.summary && (
                <p className="text-xl text-muted-foreground mb-8 pb-8 border-b">
                  {article.summary}
                </p>
              )}

              <article className="prose prose-lg max-w-none dark:prose-invert mb-12
                prose-headings:font-bold prose-headings:tracking-tight
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:pb-3
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-h4:text-xl prose-h4:mt-6 prose-h4:mb-3
                prose-p:my-4 prose-p:leading-relaxed
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:my-2
                prose-strong:text-primary prose-strong:font-semibold
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                <ArticleMarkdownBody markdown={article.body_markdown} />
              </article>

              {/* Related Interlinks from body content */}
              <RelatedInterlinks text={article.body_markdown} maxLinks={4} />

              {/* CTA Button */}
              {article.cta_button_text && article.cta_button_url && (
                <div className="flex justify-center mb-12">
                  <Button asChild size="lg" className="gap-2">
                    <a href={article.cta_button_url} target="_blank" rel="noopener noreferrer">
                      {article.cta_button_text}
                    </a>
                  </Button>
                </div>
              )}

              {/* Embedded Listings/Profiles */}
              <EmbeddedListings articleId={article.id} />

              {/* Voting Section */}
              <Card className="mb-12">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">¿Te ha resultado útil este artículo?</h3>
                  <div className="flex gap-4">
                    <Button
                      variant={userVote === 'up' ? 'default' : 'outline'}
                      onClick={() => handleVote('up')}
                      className="gap-2"
                    >
                      <ThumbsUp className="w-4 h-4" />
                      Útil ({article.votos_positivos || 0})
                    </Button>
                    <Button
                      variant={userVote === 'down' ? 'default' : 'outline'}
                      onClick={() => handleVote('down')}
                      className="gap-2"
                    >
                      <ThumbsDown className="w-4 h-4" />
                      No útil ({article.votos_negativos || 0})
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Separator className="my-12" />

              {/* Comments Section */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <MessageSquare className="w-6 h-6" />
                  Comentarios ({comments.length})
                </h2>

                <Card>
                  <CardContent className="pt-6">
                    <form onSubmit={handleSubmitComment} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          placeholder="Tu nombre *"
                          value={commentName}
                          onChange={(e) => setCommentName(e.target.value)}
                          required
                        />
                        <Input
                          type="email"
                          placeholder="Tu email (opcional)"
                          value={commentEmail}
                          onChange={(e) => setCommentEmail(e.target.value)}
                        />
                      </div>
                      <Textarea
                        placeholder="Escribe tu comentario... *"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        rows={4}
                        required
                      />
                      <Button type="submit">Enviar Comentario</Button>
                    </form>
                  </CardContent>
                </Card>

                <div className="space-y-4">
                  {comments.map((comment) => (
                    <Card key={comment.id}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold">{comment.user_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(comment.created_at).toLocaleDateString('es-ES')}
                            </p>
                          </div>
                        </div>
                        <p className="text-foreground">{comment.comment}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Related Articles Section */}
              {relatedArticles.length > 0 && (
                <>
                  <Separator className="my-12" />
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold">Artículos Relacionados</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {relatedArticles.map((relatedArticle) => (
                        <Link key={relatedArticle.id} to={`/articulo/${relatedArticle.slug}?ref=GEN-9PP3AZ`}>
                          <Card className="h-full hover:shadow-lg transition-shadow">
                            {(relatedArticle.thumbnail_url ?? relatedArticle.image_url) && (
                              <img
                                src={relatedArticle.thumbnail_url ?? relatedArticle.image_url}
                                alt={relatedArticle.title}
                                className="w-full h-48 object-cover rounded-t-lg"
                                width="400"
                                height="192"
                                loading="lazy"
                              />
                            )}
                            <CardContent className="pt-4">
                              <h3 className="font-semibold mb-2 line-clamp-2">{relatedArticle.title}</h3>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {relatedArticle.summary}
                              </p>
                              <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                {relatedArticle.reading_time} min
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Program CTA */}
              <ProgramCTA 
                categoryName={category?.name || ""} 
                variant="sidebar"
              />

              {/* Related Listings */}
              {relatedListings.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Centros Destacados
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {relatedListings.map((listing) => (
                      <Link key={listing.id} to={`/listing/${listing.slug}`}>
                        <div className="border rounded-lg p-3 hover:bg-accent transition-colors">
                          <img
                            src={
                              listing.images && listing.images.length > 0
                                ? listing.images[0]
                                : listing.thumbnail_url
                                ? listing.thumbnail_url
                                : listing.is_premium
                                ? listing.images?.[0] || defaultListingImg
                                : defaultListingImg
                            }
                            alt={listing.name}
                            className="w-full h-32 object-cover rounded-md mb-2"
                            width="400"
                            height="128"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.src = defaultListingImg;
                            }}
                          />
                          <h4 className="font-semibold text-sm mb-1 line-clamp-1">{listing.name}</h4>
                          <p className="text-xs text-muted-foreground line-clamp-1 mb-2">
                            {listing.city}
                          </p>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= (listing.rating || 5)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="text-xs text-muted-foreground ml-1">
                              ({listing.review_count || 0})
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Related Videos */}
              {relatedVideos.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Play className="w-5 h-5" />
                      Videos Relacionados
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {relatedVideos.map((video) => (
                      <Link key={video.id} to={`/video/${video.slug}?ref=GEN-9PP3AZ`}>
                        <div className="border rounded-lg overflow-hidden hover:bg-accent transition-colors">
                          <div className="relative">
                            <img
                              src={video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`}
                              alt={video.titulo}
                              className="w-full h-32 object-cover"
                              width="400"
                              height="128"
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.src = `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`;
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <Play className="w-12 h-12 text-white" />
                            </div>
                          </div>
                          <div className="p-3">
                            <h4 className="font-semibold text-sm line-clamp-2">{video.titulo}</h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              {video.vistas} vistas
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Article;